import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import RecordActions from '../../components/RecordActions';
import { useAuth } from '../../context/AuthContext';
import { useSmartBack } from '../../hooks/useSmartBack';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Separator } from '../../components/ui/separator';
import {
  ArrowLeft, Loader2, Users, Search, MapPin, CreditCard, ChevronRight,
  Plus, Briefcase, Tag, MessageSquare, DollarSign, History, UserPlus, Sparkles,
  BarChart3, ClipboardList, AlertCircle, RotateCcw, Pencil, Bell, Star, X, Image as ImageIcon, Camera, MoreHorizontal
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../../components/ui/dropdown-menu';
import DeleteCustomerDialog from '../../components/admin/DeleteCustomerDialog';
import MultiGuruSelect from '../../components/admin/MultiGuruSelect';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import UserAvatar from '../../components/UserAvatar';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EmptyEnum = { lead_sources: [], client_types: [], contact_methods: [], cleaning_frequencies: [], payment_methods: [], payment_statuses: [] };

// Canonical service list for Log Past Clean (independent of the recurring
// Frequency below). Only "what was performed" — no cadence.
const PAST_CLEAN_SERVICES = [
  'Home Clean',
  'Deep Reset Clean',
  'Airbnb Turnover',
  'Commercial Cleaning',
];

// "Why were we at the property?" — separate from Service (what we did)
// and Frequency (how often it happens).
const PAST_CLEAN_JOB_TYPES = [
  'Initial Clean',
  'Recurring Visit',
  'Walkthrough',
  'Move-In',
  'Move-Out',
  'Touch-Up',
];

// Cadence (source-of-truth for recurring schedule). "As Needed" is legacy —
// preserved on historical records but new picks use "On-Demand".
const PAST_CLEAN_FREQUENCIES = [
  'One-Time',
  'Weekly',
  'Biweekly',
  'Monthly',
  'On-Demand',
];

// ============ Tiny presentational helpers ============

const SectionHeader = ({ icon: Icon, title, action }) => (
  <div className="flex items-center justify-between mb-3">
    <h3 className="text-sm font-bold text-zinc-900 flex items-center gap-2">
      <Icon className="h-4 w-4 text-[#F56F05]" /> {title}
    </h3>
    {action}
  </div>
);

const StatPill = ({ label, value, accent }) => (
  <div className="bg-zinc-50 rounded-lg px-3 py-2">
    <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{label}</p>
    <p className={`text-lg font-bold ${accent ? 'text-[#F56F05]' : 'text-zinc-900'}`}>{value}</p>
  </div>
);

const LeadSourceBadge = ({ source }) => {
  if (!source) return <Badge variant="outline" className="bg-zinc-100 text-zinc-500 text-[10px]">No source</Badge>;
  return <Badge variant="outline" className="bg-orange-50 text-[#C2410C] border-orange-200 text-[10px]">{source}</Badge>;
};

// ============ MAIN — Customers Tab (the single hub) ============

const AdminClientsPage = () => {
  const { token, isMasterAdmin } = useAuth();
  const navigate = useNavigate();
  const { back } = useSmartBack('/admin/dashboard');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [enums, setEnums] = useState(EmptyEnum);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState('');
  const [filterLeadSource, setFilterLeadSource] = useState('all');
  const [filterClientType, setFilterClientType] = useState('all');
  const [filterCardOnFile, setFilterCardOnFile] = useState('all');
  const [filterStatus, setFilterStatus] = useState('active'); // active | archived | all

  // Dialogs
  const [addOpen, setAddOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [logPrefillClient, setLogPrefillClient] = useState(null); // pre-selects existing client when opened from profile
  const [profileOpen, setProfileOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);   // customer being edited
  const [deleteTarget, setDeleteTarget] = useState(null); // customer being deleted
  const [unreadLeads, setUnreadLeads] = useState(0);

  const fetchClients = useCallback(async () => {
    try {
      const showArchived = filterStatus !== 'active';
      const res = await axios.get(`${API_URL}/api/clients?show_archived=${showArchived}`, { headers });
      setClients(res.data);
    } catch (err) {
      toast.error('Failed to load customers');
      console.warn(err);
    } finally {
      setLoading(false);
    }
  }, [headers, filterStatus]);

  const fetchUnreadLeads = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/leads/unread-count`, { headers });
      setUnreadLeads(res.data.count || 0);
    } catch { /* silent */ }
  }, [headers]);

  const fetchEnums = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/crm/enums`, { headers });
      setEnums(res.data);
    } catch (err) {
      console.warn('Failed to load enums:', err);
    }
  }, [headers]);

  useEffect(() => {
    fetchEnums();
    fetchClients();
    fetchUnreadLeads();
  }, [fetchEnums, fetchClients, fetchUnreadLeads]);

  const filtered = useMemo(() => {
    return clients.filter(c => {
      if (search) {
        const t = search.toLowerCase();
        const matchSearch = (c.user_name || '').toLowerCase().includes(t)
          || (c.user_email || '').toLowerCase().includes(t)
          || (c.user_phone || '').toLowerCase().includes(t);
        if (!matchSearch) return false;
      }
      if (filterLeadSource !== 'all' && c.lead_source !== filterLeadSource) return false;
      if (filterClientType !== 'all' && c.client_type !== filterClientType) return false;
      if (filterCardOnFile === 'yes' && !c.has_payment_method) return false;
      if (filterCardOnFile === 'no' && c.has_payment_method) return false;
      const status = c.status || 'active';
      if (filterStatus === 'active' && status !== 'active') return false;
      if (filterStatus === 'archived' && status !== 'archived') return false;
      return true;
    });
  }, [clients, search, filterLeadSource, filterClientType, filterCardOnFile, filterStatus]);

  const openProfile = (userId) => {
    navigate(`/admin/customers/${userId}`);
  };

  const openLogPastFromProfile = (userId) => {
    setLogPrefillClient(userId);
    setLogOpen(true);
  };

  const archiveCustomer = async (userId, name) => {
    if (!window.confirm(`Archive ${name || 'this customer'}? They'll be hidden from default listings but their history is preserved.`)) return;
    try {
      await axios.post(`${API_URL}/api/admin/crm/clients/${userId}/archive`, {}, { headers });
      toast.success('Customer archived');
      fetchClients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Archive failed');
    }
  };

  const restoreCustomer = async (userId) => {
    try {
      await axios.post(`${API_URL}/api/admin/crm/clients/${userId}/restore`, {}, { headers });
      toast.success('Customer restored');
      fetchClients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Restore failed');
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-zinc-50"><Loader2 className="h-8 w-8 animate-spin text-[#F56F05]" /></div>;
  }

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-clients-page">
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 flex items-center h-16 gap-2 sm:gap-4">
          <Button variant="ghost" size="icon" onClick={back} data-testid="back-btn" className="flex-shrink-0">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg sm:text-xl font-heading text-zinc-900 truncate">Customers</h1>
          </div>
          <Button size="sm" variant="outline" onClick={() => navigate('/admin/quote-requests')} className="relative flex-shrink-0 px-2 sm:px-3" data-testid="new-leads-badge-btn" aria-label="New Leads">
            <Bell className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">New Leads</span>
            {unreadLeads > 0 && (
              <span className="ml-1 sm:ml-1.5 inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-[#F56F05] text-white text-[10px] font-bold" data-testid="new-leads-count">
                {unreadLeads}
              </span>
            )}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setAnalyticsOpen(true)} data-testid="open-analytics-btn" aria-label="Lead Sources" className="hidden sm:inline-flex flex-shrink-0">
            <BarChart3 className="h-4 w-4 mr-1" /> Lead Sources
          </Button>
          <Button size="sm" variant="outline" onClick={() => { setLogPrefillClient(null); setLogOpen(true); }} data-testid="log-past-btn" aria-label="Log Past Clean" className="hidden md:inline-flex flex-shrink-0">
            <History className="h-4 w-4 mr-1" /> Log Past Clean
          </Button>
          <Button size="sm" className="bg-[#F56F05] hover:bg-[#C2410C] text-white flex-shrink-0 px-2 sm:px-3" onClick={() => setAddOpen(true)} data-testid="add-customer-btn" aria-label="Add client">
            <UserPlus className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">Add client</span>
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="ghost" className="md:hidden flex-shrink-0 h-9 w-9 p-0" data-testid="customers-more-menu" aria-label="More actions">
                <MoreHorizontal className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem onClick={() => setAnalyticsOpen(true)} data-testid="menu-lead-sources">
                <BarChart3 className="h-4 w-4 mr-2" /> Lead Sources
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { setLogPrefillClient(null); setLogOpen(true); }} data-testid="menu-log-past">
                <History className="h-4 w-4 mr-2" /> Log Past Clean
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        {/* Search + Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-zinc-100 p-3 flex flex-col md:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <Input placeholder="Search name, email, phone..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="search-input" />
          </div>
          <Select value={filterLeadSource} onValueChange={setFilterLeadSource}>
            <SelectTrigger className="w-full md:w-44" data-testid="filter-lead-source"><SelectValue placeholder="Lead Source" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              {enums.lead_sources.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterClientType} onValueChange={setFilterClientType}>
            <SelectTrigger className="w-full md:w-40" data-testid="filter-client-type"><SelectValue placeholder="Customer Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {enums.client_types.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterCardOnFile} onValueChange={setFilterCardOnFile}>
            <SelectTrigger className="w-full md:w-36" data-testid="filter-card"><SelectValue placeholder="Card on File" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All cards</SelectItem>
              <SelectItem value="yes">Has card</SelectItem>
              <SelectItem value="no">No card</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-full md:w-32" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Customer list */}
        {filtered.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-12 text-center">
              <Users className="h-12 w-12 text-zinc-300 mx-auto mb-3" />
              <p className="text-zinc-500">No customers match these filters</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {filtered.map((c, i) => {
              const isArchived = c.status === 'archived';
              return (
              <motion.div key={c.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}>
                <Card className={`border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer ${isArchived ? 'opacity-70' : ''}`} onClick={() => openProfile(c.user_id)} data-testid={`customer-row-${c.id}`}>
                  <CardContent className="p-3 sm:p-4">
                    <div className="flex items-start sm:items-center gap-3 sm:gap-4">
                      <UserAvatar user={{ full_name: c.user_name, photo_url: c.user_photo_url, role: 'client' }} size="md" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <p className="font-medium text-zinc-900 truncate">{c.user_name || 'N/A'}</p>
                          {c.is_manual && <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 text-[10px]">Concierge</Badge>}
                          {isArchived && <Badge variant="outline" className="bg-zinc-200 text-zinc-700 border-zinc-300 text-[10px]" data-testid={`archived-badge-${c.user_id}`}>Archived</Badge>}
                        </div>
                        <p className="text-sm text-zinc-500 truncate">
                          {c.user_phone || c.user_email}
                          {c.client_type && <span className="text-zinc-400"> · {c.client_type}</span>}
                        </p>
                        {/* Mobile: badges stack below name */}
                        <div className="flex sm:hidden items-center gap-2 flex-wrap mt-2" onClick={(e) => e.stopPropagation()}>
                          <LeadSourceBadge source={c.lead_source} />
                          {c.has_payment_method
                            ? <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">Card</Badge>
                            : <Badge variant="outline" className="bg-zinc-100 text-zinc-500 text-xs">No card</Badge>}
                        </div>
                      </div>
                      {/* Desktop: badges inline */}
                      <div className="hidden sm:flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                        <LeadSourceBadge source={c.lead_source} />
                        {c.has_payment_method
                          ? <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">Card</Badge>
                          : <Badge variant="outline" className="bg-zinc-100 text-zinc-500 text-xs">No card</Badge>}
                        {isArchived ? (
                          <Button size="sm" variant="ghost" className="h-8 px-2 text-xs"
                            onClick={() => restoreCustomer(c.user_id)}
                            data-testid={`restore-customer-${c.user_id}`}>
                            <RotateCcw className="h-3.5 w-3.5 mr-1" /> Restore
                          </Button>
                        ) : (
                          <RecordActions
                            canEdit={true}
                            canArchive={true}
                            canDelete={true}
                            onEdit={() => setEditTarget(c)}
                            onArchive={() => archiveCustomer(c.user_id, c.user_name)}
                            onCustomDelete={() => setDeleteTarget(c)}
                            onDeleted={fetchClients}
                            headers={headers}
                            testIdPrefix="customer"
                            entityId={c.user_id}
                          />
                        )}
                        <ChevronRight className="h-4 w-4 text-zinc-300" />
                      </div>
                      {/* Mobile: compact action button */}
                      <div className="flex sm:hidden flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                        {isArchived ? (
                          <Button size="sm" variant="ghost" className="h-8 px-2 text-xs"
                            onClick={() => restoreCustomer(c.user_id)}
                            data-testid={`restore-customer-mobile-${c.user_id}`}>
                            <RotateCcw className="h-3.5 w-3.5" />
                          </Button>
                        ) : (
                          <RecordActions
                            canEdit={true}
                            canArchive={true}
                            canDelete={true}
                            onEdit={() => setEditTarget(c)}
                            onArchive={() => archiveCustomer(c.user_id, c.user_name)}
                            onCustomDelete={() => setDeleteTarget(c)}
                            onDeleted={fetchClients}
                            headers={headers}
                            testIdPrefix="customer-mobile"
                            entityId={c.user_id}
                          />
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
              );
            })}
          </div>
        )}
      </main>

      {addOpen && <AddCustomerDialog enums={enums} headers={headers} onClose={() => setAddOpen(false)} onCreated={fetchClients} />}
      {logOpen && <LogPastCleanDialog enums={enums} headers={headers} clients={clients} prefillClientId={logPrefillClient} onClose={() => { setLogOpen(false); setLogPrefillClient(null); }} onLogged={() => { fetchClients(); }} />}
      {profileOpen && selectedClientId && <CustomerProfileDialog clientUserId={selectedClientId} enums={enums} headers={headers} onClose={() => setProfileOpen(false)} onRefresh={fetchClients} onLogPastClick={openLogPastFromProfile} />}
      {analyticsOpen && <LeadSourceAnalyticsDialog headers={headers} onClose={() => setAnalyticsOpen(false)} />}
      {editTarget && <EditCustomerDialog customer={editTarget} enums={enums} headers={headers} onClose={() => setEditTarget(null)} onSaved={() => { setEditTarget(null); fetchClients(); toast.success('Customer updated'); }} />}
      <DeleteCustomerDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        customer={deleteTarget}
        headers={headers}
        isMasterAdmin={isMasterAdmin}
        onDeleted={fetchClients}
      />
    </div>
  );
};

// ============ Add Customer Dialog ============

const AddCustomerDialog = ({ enums, headers, onClose, onCreated }) => {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    full_name: '', email: '', phone: '',
    preferred_contact_method: '', lead_source: '', lead_source_notes: '', client_type: '', notes: '',
    address_line1: '', address_line2: '', city: '', state: '', zip: '',
    access_notes: '', parking_notes: '', gate_code: '', pet_notes: '',
    cleaning_frequency: '', estimated_duration_minutes: '', agreed_price: '', internal_notes: ''
  });

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.full_name || !form.lead_source || !form.client_type) {
      toast.error('Full name, lead source, and customer type are required');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach(k => { if (payload[k] === '') delete payload[k]; });
      if (payload.agreed_price) payload.agreed_price = parseFloat(payload.agreed_price);
      if (payload.estimated_duration_minutes) payload.estimated_duration_minutes = parseInt(payload.estimated_duration_minutes);
      await axios.post(`${API_URL}/api/admin/crm/clients`, payload, { headers });
      toast.success('Customer added');
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create customer');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Customer</DialogTitle>
          <DialogDescription>For outside leads — TaskRabbit, Yelp, Google, referrals, phone/text bookings, etc. No password is set; the account is admin-managed until converted.</DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="info" className="mt-2">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="info" data-testid="tab-info">Customer Info</TabsTrigger>
            <TabsTrigger value="address" data-testid="tab-address">Service Address</TabsTrigger>
            <TabsTrigger value="service" data-testid="tab-service">Service Details</TabsTrigger>
          </TabsList>

          <TabsContent value="info" className="space-y-3 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Full Name *</Label><Input value={form.full_name} onChange={e => upd('full_name', e.target.value)} data-testid="ac-name" /></div>
              <div><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={e => upd('phone', e.target.value)} placeholder="(503) 555-0188" data-testid="ac-phone" /></div>
              <div><Label className="text-xs">Email (optional)</Label><Input type="email" value={form.email} onChange={e => upd('email', e.target.value)} placeholder="leave blank if unknown" data-testid="ac-email" /></div>
              <div>
                <Label className="text-xs">Preferred Contact</Label>
                <Select value={form.preferred_contact_method} onValueChange={v => upd('preferred_contact_method', v)}>
                  <SelectTrigger data-testid="ac-contact"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>{enums.contact_methods.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Lead Source *</Label>
                <Select value={form.lead_source} onValueChange={v => upd('lead_source', v)}>
                  <SelectTrigger data-testid="ac-lead-source"><SelectValue placeholder="Required" /></SelectTrigger>
                  <SelectContent>{enums.lead_sources.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Customer Type *</Label>
                <Select value={form.client_type} onValueChange={v => upd('client_type', v)}>
                  <SelectTrigger data-testid="ac-client-type"><SelectValue placeholder="Required" /></SelectTrigger>
                  <SelectContent>{enums.client_types.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-xs">Lead Source Notes</Label><Input value={form.lead_source_notes} onChange={e => upd('lead_source_notes', e.target.value)} placeholder='e.g., "Found via Yelp ad, mentioned competitor"' data-testid="ac-source-notes" /></div>
            <div><Label className="text-xs">General Notes</Label><Textarea rows={3} value={form.notes} onChange={e => upd('notes', e.target.value)} placeholder="Anything to remember about this customer..." data-testid="ac-notes" /></div>
          </TabsContent>

          <TabsContent value="address" className="space-y-3 mt-4">
            <div><Label className="text-xs">Street Address</Label><Input value={form.address_line1} onChange={e => upd('address_line1', e.target.value)} data-testid="ac-addr1" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Unit / Apt</Label><Input value={form.address_line2} onChange={e => upd('address_line2', e.target.value)} data-testid="ac-addr2" /></div>
              <div className="col-span-2"><Label className="text-xs">City</Label><Input value={form.city} onChange={e => upd('city', e.target.value)} data-testid="ac-city" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">State</Label><Input value={form.state} onChange={e => upd('state', e.target.value)} placeholder="OR" data-testid="ac-state" /></div>
              <div><Label className="text-xs">ZIP</Label><Input value={form.zip} onChange={e => upd('zip', e.target.value)} data-testid="ac-zip" /></div>
            </div>
            <div><Label className="text-xs">Access Instructions</Label><Textarea rows={2} value={form.access_notes} onChange={e => upd('access_notes', e.target.value)} placeholder="e.g., side door, key under mat" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Parking Notes</Label><Input value={form.parking_notes} onChange={e => upd('parking_notes', e.target.value)} /></div>
              <div><Label className="text-xs">Gate / Lockbox Code</Label><Input value={form.gate_code} onChange={e => upd('gate_code', e.target.value)} /></div>
            </div>
            <div><Label className="text-xs">Pet Notes</Label><Input value={form.pet_notes} onChange={e => upd('pet_notes', e.target.value)} placeholder="Cat upstairs, friendly dog" /></div>
          </TabsContent>

          <TabsContent value="service" className="space-y-3 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Cleaning Frequency</Label>
                <Select value={form.cleaning_frequency} onValueChange={v => upd('cleaning_frequency', v)}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>{enums.cleaning_frequencies.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Agreed Price ($)</Label><Input type="number" step="0.01" value={form.agreed_price} onChange={e => upd('agreed_price', e.target.value)} data-testid="ac-price" /></div>
              <div><Label className="text-xs">Estimated Duration (min)</Label><Input type="number" value={form.estimated_duration_minutes} onChange={e => upd('estimated_duration_minutes', e.target.value)} placeholder="120" /></div>
            </div>
            <div><Label className="text-xs">Internal Notes (admin only)</Label><Textarea rows={3} value={form.internal_notes} onChange={e => upd('internal_notes', e.target.value)} placeholder="Pricing rationale, special instructions..." /></div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-2 pt-4 border-t mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#F56F05] hover:bg-[#C2410C] text-white" onClick={submit} disabled={saving} data-testid="ac-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />} Create Customer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============ Log Past Clean Dialog ============

const LogPastCleanDialog = ({ enums, headers, clients, prefillClientId, onClose, onLogged }) => {
  const [saving, setSaving] = useState(false);
  const [cleaners, setCleaners] = useState([]);
  const [clientMode, setClientMode] = useState(prefillClientId ? 'existing' : 'existing');
  const [form, setForm] = useState({
    existing_client_user_id: prefillClientId || '',
    new_client: { full_name: '', phone: '', lead_source: '', client_type: '' },
    service_name_override: '',
    job_type: '',
    date_completed: new Date().toISOString().slice(0, 10),
    start_time: '09:00',
    end_time: '11:00',
    total_hours: '',
    assigned_cleaner_user_ids: [],
    cleaning_frequency: '',
    address_line1: '', city: '', state: '', zip: '',
    amount_charged: '',
    amount_paid: '',
    payment_method: '',
    payment_status: '',
    work_completed: '',
    client_feedback: '',
    issues_encountered: '',
    supplies_used: '',
    internal_notes: ''
  });

  useEffect(() => {
    axios.get(`${API_URL}/api/cleaners`, { headers })
      .then(r => setCleaners(r.data))
      .catch(err => console.warn('Failed to load cleaners:', err));
  }, [headers]);

  // Recurring Visit auto-frequency: when admin picks Job Type = "Recurring Visit"
  // and an existing customer with a saved cadence is selected, prefill Frequency
  // from that profile so recurring logs go faster. Only fills when currently empty
  // so we never overwrite an explicit admin choice.
  useEffect(() => {
    if (form.job_type !== 'Recurring Visit') return;
    if (form.cleaning_frequency) return;
    if (clientMode !== 'existing' || !form.existing_client_user_id) return;
    const client = clients.find(c => c.user_id === form.existing_client_user_id);
    let cadence = client?.cleaning_frequency;
    if (!cadence) return;
    // Legacy "As Needed" → new "On-Demand"
    if (cadence === 'As Needed') cadence = 'On-Demand';
    if (!PAST_CLEAN_FREQUENCIES.includes(cadence)) return;
    setForm(f => ({ ...f, cleaning_frequency: cadence }));
    toast.info(`Frequency prefilled from ${client.user_name || 'customer'}'s profile: ${cadence}`);
  }, [form.job_type, form.existing_client_user_id, form.cleaning_frequency, clientMode, clients]);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const updNew = (k, v) => setForm(f => ({ ...f, new_client: { ...f.new_client, [k]: v } }));

  const submit = async () => {
    const isWalkthrough = form.job_type === 'Walkthrough';
    if (!isWalkthrough) {
      if (!form.amount_charged || !form.payment_method || !form.payment_status) {
        toast.error('Amount, payment method, and status are required'); return;
      }
    }
    if (clientMode === 'existing' && !form.existing_client_user_id) {
      toast.error('Select an existing customer'); return;
    }
    if (clientMode === 'new' && (!form.new_client.full_name || !form.new_client.lead_source || !form.new_client.client_type)) {
      toast.error('New customer: name, lead source, and type required'); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        // PART 11 — a Walkthrough is a property visit, not a cleaning revenue event.
        // Log $0 with an Other/Paid marker so the appointment records cleanly, but
        // this row must NOT be counted as cleaning revenue elsewhere.
        amount_charged: isWalkthrough && !form.amount_charged
          ? 0
          : parseFloat(form.amount_charged),
        payment_method: isWalkthrough && !form.payment_method ? 'Other' : form.payment_method,
        payment_status: isWalkthrough && !form.payment_status ? 'Paid' : form.payment_status,
        amount_paid: form.amount_paid ? parseFloat(form.amount_paid) : undefined,
        total_hours: form.total_hours ? parseFloat(form.total_hours) : undefined,
      };
      if (clientMode === 'existing') {
        delete payload.new_client;
      } else {
        delete payload.existing_client_user_id;
        Object.keys(payload.new_client).forEach(k => { if (payload.new_client[k] === '') delete payload.new_client[k]; });
      }
      Object.keys(payload).forEach(k => { if (payload[k] === '' || payload[k] === undefined) delete payload[k]; });
      await axios.post(`${API_URL}/api/admin/crm/log-past-clean`, payload, { headers });
      toast.success(isWalkthrough ? 'Walkthrough logged' : 'Past clean logged');
      onLogged();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to log past clean');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Log Past Clean</DialogTitle>
          <DialogDescription>Backdate a completed cleaning. Creates a Completed appointment + invoice with manual payment record on this customer&apos;s service history.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="bg-zinc-50 rounded-lg p-3">
            <SectionHeader icon={Users} title="Customer" />
            <div className="flex gap-2 mb-3">
              <Button size="sm" variant={clientMode === 'existing' ? 'default' : 'outline'} className={clientMode === 'existing' ? 'bg-[#F56F05] hover:bg-[#C2410C] text-white' : ''} onClick={() => setClientMode('existing')} data-testid="mode-existing">Existing Customer</Button>
              <Button size="sm" variant={clientMode === 'new' ? 'default' : 'outline'} className={clientMode === 'new' ? 'bg-[#F56F05] hover:bg-[#C2410C] text-white' : ''} onClick={() => setClientMode('new')} data-testid="mode-new">Create New</Button>
            </div>
            {clientMode === 'existing' ? (
              <Select value={form.existing_client_user_id} onValueChange={v => upd('existing_client_user_id', v)}>
                <SelectTrigger data-testid="lp-existing-client"><SelectValue placeholder="Select customer..." /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {clients.map(c => <SelectItem key={c.user_id} value={c.user_id}>{c.user_name} · {c.user_phone || c.user_email}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Full Name *" value={form.new_client.full_name} onChange={e => updNew('full_name', e.target.value)} data-testid="lp-new-name" />
                <Input placeholder="Phone" value={form.new_client.phone} onChange={e => updNew('phone', e.target.value)} />
                <Select value={form.new_client.lead_source} onValueChange={v => updNew('lead_source', v)}>
                  <SelectTrigger data-testid="lp-new-source"><SelectValue placeholder="Lead Source *" /></SelectTrigger>
                  <SelectContent>{enums.lead_sources.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={form.new_client.client_type} onValueChange={v => updNew('client_type', v)}>
                  <SelectTrigger data-testid="lp-new-type"><SelectValue placeholder="Customer Type *" /></SelectTrigger>
                  <SelectContent>{enums.client_types.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="bg-zinc-50 rounded-lg p-3">
            <SectionHeader icon={Briefcase} title="Job Details" />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Service</Label>
                <Select value={form.service_name_override} onValueChange={v => upd('service_name_override', v)}>
                  <SelectTrigger data-testid="lp-service"><SelectValue placeholder="Select service..." /></SelectTrigger>
                  <SelectContent>
                    {PAST_CLEAN_SERVICES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Job Type</Label>
                <Select value={form.job_type} onValueChange={v => upd('job_type', v)}>
                  <SelectTrigger data-testid="lp-job-type"><SelectValue placeholder="Why we were there..." /></SelectTrigger>
                  <SelectContent>
                    {PAST_CLEAN_JOB_TYPES.map(j => <SelectItem key={j} value={j}>{j}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Frequency</Label>
                <Select value={form.cleaning_frequency} onValueChange={v => upd('cleaning_frequency', v)}>
                  <SelectTrigger data-testid="lp-frequency"><SelectValue placeholder="How often..." /></SelectTrigger>
                  <SelectContent>
                    {PAST_CLEAN_FREQUENCIES.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Date Completed *</Label><Input type="date" value={form.date_completed} onChange={e => upd('date_completed', e.target.value)} data-testid="lp-date" /></div>
              <div><Label className="text-xs">Total Hours (optional)</Label><Input type="number" step="0.25" value={form.total_hours} onChange={e => upd('total_hours', e.target.value)} placeholder="auto if start/end set" /></div>
              <div><Label className="text-xs">Start Time</Label><Input type="time" value={form.start_time} onChange={e => upd('start_time', e.target.value)} /></div>
              <div><Label className="text-xs">End Time</Label><Input type="time" value={form.end_time} onChange={e => upd('end_time', e.target.value)} /></div>
              <div className="col-span-2">
                <Label className="text-xs">Assigned Team</Label>
                <MultiGuruSelect
                  value={form.assigned_cleaner_user_ids}
                  onChange={v => upd('assigned_cleaner_user_ids', v)}
                  headers={headers}
                  gurusOverride={cleaners}
                  testIdPrefix="lp-team"
                  placeholder="Assign gurus…"
                />
              </div>
            </div>
          </div>

          <div className="bg-zinc-50 rounded-lg p-3">
            <SectionHeader icon={DollarSign} title="Payment" />
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Amount Charged ($) *</Label><Input type="number" step="0.01" value={form.amount_charged} onChange={e => upd('amount_charged', e.target.value)} data-testid="lp-charged" /></div>
              <div><Label className="text-xs">Amount Paid ($)</Label><Input type="number" step="0.01" value={form.amount_paid} onChange={e => upd('amount_paid', e.target.value)} placeholder="defaults to charged if Paid" /></div>
              <div>
                <Label className="text-xs">Payment Method *</Label>
                <Select value={form.payment_method} onValueChange={v => upd('payment_method', v)}>
                  <SelectTrigger data-testid="lp-pay-method"><SelectValue placeholder="Required" /></SelectTrigger>
                  <SelectContent>{enums.payment_methods.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Payment Status *</Label>
                <Select value={form.payment_status} onValueChange={v => upd('payment_status', v)}>
                  <SelectTrigger data-testid="lp-pay-status"><SelectValue placeholder="Required" /></SelectTrigger>
                  <SelectContent>{enums.payment_statuses.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="bg-zinc-50 rounded-lg p-3 space-y-2">
            <SectionHeader icon={MessageSquare} title="Job Notes" />
            <div><Label className="text-xs">Work Completed</Label><Textarea rows={2} value={form.work_completed} onChange={e => upd('work_completed', e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Customer Feedback</Label><Textarea rows={2} value={form.client_feedback} onChange={e => upd('client_feedback', e.target.value)} /></div>
              <div><Label className="text-xs">Issues Encountered</Label><Textarea rows={2} value={form.issues_encountered} onChange={e => upd('issues_encountered', e.target.value)} /></div>
              <div><Label className="text-xs">Supplies Used</Label><Textarea rows={2} value={form.supplies_used} onChange={e => upd('supplies_used', e.target.value)} /></div>
              <div><Label className="text-xs">Internal Notes</Label><Textarea rows={2} value={form.internal_notes} onChange={e => upd('internal_notes', e.target.value)} /></div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#F56F05] hover:bg-[#C2410C] text-white" onClick={submit} disabled={saving} data-testid="lp-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ClipboardList className="h-4 w-4 mr-2" />} Log Past Clean
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============ Customer Profile Dialog ============

const PastCleanRow = ({ appt, headers, enums, onChanged }) => {
  const [editOpen, setEditOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isManual = !!appt.manually_logged;

  const handleDelete = async () => {
    if (!window.confirm('Permanently delete this logged clean? This removes the appointment and any related invoice. This cannot be undone.')) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/admin/crm/past-clean/${appt.id}`, { headers });
      toast.success('Past clean deleted');
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally { setDeleting(false); }
  };

  return (
    <>
      <div className="flex items-center justify-between bg-zinc-50 rounded-lg px-3 py-2 text-sm gap-2" data-testid={`past-clean-row-${appt.id}`}>
        <div className="min-w-0 flex-1">
          <p className="font-medium truncate">
            {new Date(appt.start_time).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            {appt.service_name_override && <span className="text-zinc-500 font-normal"> · {appt.service_name_override}</span>}
          </p>
          <p className="text-xs text-zinc-500 truncate">
            {appt.notes_internal ? appt.notes_internal.slice(0, 80) : (appt.work_completed ? appt.work_completed.slice(0, 80) : 'Standard cleaning')}
          </p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {isManual && <Badge variant="outline" className="bg-purple-50 text-purple-700 text-[10px]">Manual</Badge>}
          <Badge variant="outline" className={`text-xs ${appt.status === 'Completed' ? 'bg-green-50 text-green-700' : appt.status === 'Canceled' ? 'bg-red-50 text-red-700' : 'bg-zinc-100 text-zinc-600'}`}>{appt.status}</Badge>
          {isManual && (
            <>
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-zinc-500 hover:text-[#F56F05]"
                onClick={() => setEditOpen(true)} data-testid={`past-clean-edit-${appt.id}`}>
                Edit
              </Button>
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-zinc-500 hover:text-red-600"
                onClick={handleDelete} disabled={deleting} data-testid={`past-clean-delete-${appt.id}`}>
                {deleting ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Delete'}
              </Button>
            </>
          )}
        </div>
      </div>
      {editOpen && (
        <PastCleanEditDialog
          appt={appt}
          headers={headers}
          enums={enums}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); onChanged(); }}
        />
      )}
    </>
  );
};

const PastCleanEditDialog = ({ appt, headers, enums, onClose, onSaved }) => {
  const initialDate = appt.start_time ? new Date(appt.start_time).toISOString().slice(0, 10) : '';
  const legacyService = appt.service_name_override || '';
  const isLegacyService = legacyService && !PAST_CLEAN_SERVICES.includes(legacyService);
  const legacyFreq = appt.cleaning_frequency || '';
  const isLegacyFreq = legacyFreq && !PAST_CLEAN_FREQUENCIES.includes(legacyFreq);
  const legacyJobType = appt.job_type || '';
  const isLegacyJobType = legacyJobType && !PAST_CLEAN_JOB_TYPES.includes(legacyJobType);
  const [form, setForm] = useState({
    date_completed: initialDate,
    service_name_override: legacyService,
    job_type: legacyJobType,
    cleaning_frequency: legacyFreq,
    amount_charged: appt.amount ?? '',
    work_completed: appt.work_completed || '',
    client_feedback: appt.client_feedback || '',
    issues_encountered: appt.issues_encountered || '',
    internal_notes: appt.notes_internal || '',
    assigned_cleaner_user_ids: null, // null = untouched
  });
  const [saving, setSaving] = useState(false);
  const [teamLoaded, setTeamLoaded] = useState(false);
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  // Lazy-load the current team once the dialog opens so opening the editor is
  // cheap for admins who don't intend to touch the team.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/api/admin/assignments/appointment/${appt.id}`, { headers })
      .then(r => {
        if (cancelled) return;
        const ids = (r.data?.current_team || []).map(t => t.staff_profile_id);
        setForm(prev => ({ ...prev, assigned_cleaner_user_ids: ids }));
        setTeamLoaded(true);
      })
      .catch(() => { setTeamLoaded(true); });
    return () => { cancelled = true; };
  }, [appt.id, headers]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.amount_charged === '') delete payload.amount_charged;
      else payload.amount_charged = parseFloat(payload.amount_charged);
      if (payload.cleaning_frequency === '') delete payload.cleaning_frequency;
      if (payload.service_name_override === '') delete payload.service_name_override;
      if (payload.job_type === '') delete payload.job_type;
      // Only include team if it's been loaded (and possibly changed) by the admin.
      // null means "leave the team unchanged".
      if (payload.assigned_cleaner_user_ids === null) delete payload.assigned_cleaner_user_ids;
      await axios.patch(`${API_URL}/api/admin/crm/past-clean/${appt.id}`, payload, { headers });
      toast.success('Past clean updated');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="past-clean-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit logged clean</DialogTitle>
          <DialogDescription>
            Correct the service, job type, and frequency. Linked invoice + appointment stay attached — no duplicates are created.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-zinc-500">Date</Label>
              <Input type="date" value={form.date_completed}
                onChange={e => set('date_completed', e.target.value)}
                className="rounded-xl border-zinc-200" data-testid="past-clean-edit-date" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Amount charged ($)</Label>
              <Input type="number" step="0.01" value={form.amount_charged}
                onChange={e => set('amount_charged', e.target.value)}
                className="rounded-xl border-zinc-200" data-testid="past-clean-edit-amount" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-zinc-500">Service</Label>
              <Select value={form.service_name_override} onValueChange={v => set('service_name_override', v)}>
                <SelectTrigger data-testid="past-clean-edit-service"><SelectValue placeholder="Select service..." /></SelectTrigger>
                <SelectContent>
                  {isLegacyService && (
                    <SelectItem value={legacyService} disabled className="text-zinc-400 italic">
                      {legacyService} (legacy — replace)
                    </SelectItem>
                  )}
                  {PAST_CLEAN_SERVICES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              {isLegacyService && (
                <p className="text-[11px] text-amber-600 mt-1">Legacy value: pick a new option to correct this record.</p>
              )}
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Job Type</Label>
              <Select value={form.job_type} onValueChange={v => set('job_type', v)}>
                <SelectTrigger data-testid="past-clean-edit-job-type"><SelectValue placeholder="Why we were there..." /></SelectTrigger>
                <SelectContent>
                  {isLegacyJobType && (
                    <SelectItem value={legacyJobType} disabled className="text-zinc-400 italic">
                      {legacyJobType} (legacy)
                    </SelectItem>
                  )}
                  {PAST_CLEAN_JOB_TYPES.map(j => <SelectItem key={j} value={j}>{j}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs text-zinc-500">Frequency</Label>
            <Select value={form.cleaning_frequency} onValueChange={v => set('cleaning_frequency', v)}>
              <SelectTrigger data-testid="past-clean-edit-frequency"><SelectValue placeholder="How often..." /></SelectTrigger>
              <SelectContent>
                {isLegacyFreq && (
                  <SelectItem value={legacyFreq} disabled className="text-zinc-400 italic">
                    {legacyFreq} (legacy — replace)
                  </SelectItem>
                )}
                {PAST_CLEAN_FREQUENCIES.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
              </SelectContent>
            </Select>
            {legacyFreq === 'As Needed' && (
              <p className="text-[11px] text-amber-600 mt-1">This record uses the legacy &quot;As Needed&quot; label — the new equivalent is &quot;On-Demand&quot;.</p>
            )}
          </div>
          <div>
            <Label className="text-xs text-zinc-500">Assigned Team</Label>
            {teamLoaded ? (
              <MultiGuruSelect
                value={form.assigned_cleaner_user_ids || []}
                onChange={v => set('assigned_cleaner_user_ids', v)}
                headers={headers}
                testIdPrefix="past-clean-edit-team"
                placeholder="Assign gurus…"
              />
            ) : (
              <p className="text-[11px] text-zinc-400 italic py-2">Loading team…</p>
            )}
          </div>
          <div>
            <Label className="text-xs text-zinc-500">Work completed</Label>
            <textarea value={form.work_completed}
              onChange={e => set('work_completed', e.target.value)}
              rows={2}
              className="w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm"
              data-testid="past-clean-edit-work" />
          </div>
          <div>
            <Label className="text-xs text-zinc-500">Internal notes</Label>
            <textarea value={form.internal_notes}
              onChange={e => set('internal_notes', e.target.value)}
              rows={2}
              className="w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm"
              data-testid="past-clean-edit-notes" />
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
            <Button className="flex-1 bg-[#F56F05] hover:bg-[#e06505] text-white"
              onClick={save} disabled={saving} data-testid="past-clean-edit-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save changes
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const CustomerProfileDialog = ({ clientUserId, enums, headers, onClose, onRefresh, onLogPastClick }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noteBody, setNoteBody] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [converting, setConverting] = useState(false);
  const [requestingCard, setRequestingCard] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [addressDialog, setAddressDialog] = useState(null); // { mode: 'add'|'edit', address? }

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/crm/clients/${clientUserId}/full`, { headers });
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load profile');
      console.warn(err);
    } finally { setLoading(false); }
  }, [clientUserId, headers]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const addNote = async () => {
    if (!noteBody.trim()) return;
    setSavingNote(true);
    try {
      await axios.post(`${API_URL}/api/admin/crm/clients/${clientUserId}/notes`, { body: noteBody }, { headers });
      setNoteBody('');
      await fetchProfile();
      toast.success('Note added');
    } catch (err) { toast.error('Failed to add note'); }
    finally { setSavingNote(false); }
  };

  const convert = async () => {
    if (!window.confirm('Send this customer a card-capture link and move them into the standard workflow?')) return;
    setConverting(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/crm/clients/${clientUserId}/convert`, {}, { headers });
      toast.success('Customer converted — card capture link generated');
      const fullUrl = `${window.location.origin}${res.data.card_capture_url}`;
      try { await navigator.clipboard.writeText(fullUrl); toast.success('Link copied to clipboard'); } catch (err) { console.warn('Clipboard write failed:', err); }
      await fetchProfile();
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to convert');
    } finally { setConverting(false); }
  };

  const requestCard = async () => {
    setRequestingCard(true);
    try {
      const res = await axios.post(`${API_URL}/api/card-capture/admin/send`, {
        client_user_id: clientUserId,
      }, { headers });
      toast.success('Card capture link generated');
      const url = res.data.card_capture_url || res.data.url;
      if (url) {
        const fullUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`;
        try { await navigator.clipboard.writeText(fullUrl); toast.success('Link copied to clipboard'); } catch (err) { console.warn('Clipboard write failed:', err); }
      }
      await fetchProfile();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send card capture link');
    } finally {
      setRequestingCard(false);
    }
  };

  if (loading || !data) {
    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="max-w-3xl"><div className="flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#F56F05]" /></div></DialogContent>
      </Dialog>
    );
  }

  const p = data.profile;
  const s = data.summary;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 pr-8">
            <UserAvatar user={{ full_name: p.user_name, photo_url: p.user_photo_url, role: 'client' }} size="md" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="truncate">{p.user_name}</span>
                {p.is_manual && <Badge variant="outline" className="bg-purple-50 text-purple-700 text-[10px]">Concierge</Badge>}
                <LeadSourceBadge source={p.lead_source} />
                {p.client_type && <Badge variant="outline" className="text-[10px]">{p.client_type}</Badge>}
              </div>
              <DialogDescription className="text-xs mt-0.5 truncate">{p.user_email} · {p.user_phone || 'no phone'}</DialogDescription>
            </div>
            <Button
              size="sm"
              onClick={() => setEditOpen(true)}
              className="bg-[#F56F05] hover:bg-[#C2410C] text-white flex-shrink-0"
              data-testid="edit-customer-btn"
            >
              <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit Customer
            </Button>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-4 gap-2 my-3">
          <StatPill label="Jobs" value={s.total_appointments} />
          <StatPill label="Completed" value={s.completed_count} />
          <StatPill label="Revenue" value={`$${s.total_revenue_paid.toFixed(0)}`} accent />
          <StatPill label="Unpaid" value={`$${s.unpaid_balance.toFixed(0)}`} />
        </div>

        <div className={`rounded-lg p-3 mb-3 flex items-center justify-between gap-2 flex-wrap ${p.has_payment_method ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
          <div className="flex items-center gap-2 text-sm">
            <CreditCard className={`h-4 w-4 ${p.has_payment_method ? 'text-green-700' : 'text-amber-700'}`} />
            <span className={p.has_payment_method ? 'text-green-800' : 'text-amber-800'}>
              {p.has_payment_method ? 'Card on file' : 'No card on file'}
            </span>
            {p.auto_pay_enabled && <Badge className="bg-green-600 text-white text-[10px]">Auto-pay</Badge>}
          </div>
          <div className="flex items-center gap-2">
            {!p.has_payment_method && (
              <Button size="sm" variant="outline" onClick={requestCard} disabled={requestingCard} data-testid="request-card-btn">
                {requestingCard ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <CreditCard className="h-3 w-3 mr-1" />} Request Card
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => onLogPastClick(clientUserId)} data-testid="log-past-from-profile">
              <History className="h-3 w-3 mr-1" /> Log Past Clean
            </Button>
            {p.is_manual && (
              <Button size="sm" className="bg-[#F56F05] hover:bg-[#C2410C] text-white" onClick={convert} disabled={converting} data-testid="convert-btn">
                {converting ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Sparkles className="h-3 w-3 mr-1" />} Convert to Gleam Guru
              </Button>
            )}
          </div>
        </div>

        <Tabs defaultValue="overview">
          <TabsList className="grid grid-cols-5 w-full">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="history">History ({data.appointments.length})</TabsTrigger>
            <TabsTrigger value="invoices">Invoices ({data.invoices.length})</TabsTrigger>
            <TabsTrigger value="photos">Photos ({data.photos.length})</TabsTrigger>
            <TabsTrigger value="notes">Notes ({data.notes.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-3 mt-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-start gap-2"><Tag className="h-4 w-4 text-zinc-400 mt-0.5" /><div><p className="text-zinc-500 text-xs">Lead Source</p><p className="font-medium">{p.lead_source || '—'}</p>{p.lead_source_notes && <p className="text-xs text-zinc-400 mt-0.5">{p.lead_source_notes}</p>}</div></div>
              <div className="flex items-start gap-2"><Users className="h-4 w-4 text-zinc-400 mt-0.5" /><div><p className="text-zinc-500 text-xs">Customer Type</p><p className="font-medium">{p.client_type || '—'}</p></div></div>
              <div className="flex items-start gap-2"><MessageSquare className="h-4 w-4 text-zinc-400 mt-0.5" /><div><p className="text-zinc-500 text-xs">Preferred Contact</p><p className="font-medium">{p.preferred_contact_method || '—'}</p></div></div>
              <div className="flex items-start gap-2"><History className="h-4 w-4 text-zinc-400 mt-0.5" /><div><p className="text-zinc-500 text-xs">Frequency</p><p className="font-medium">{p.cleaning_frequency || '—'}</p></div></div>
              <div className="flex items-start gap-2"><DollarSign className="h-4 w-4 text-zinc-400 mt-0.5" /><div><p className="text-zinc-500 text-xs">Agreed Price</p><p className="font-medium">{p.agreed_price ? `$${p.agreed_price}` : '—'}</p></div></div>
            </div>

            <Separator />
            <div>
              <SectionHeader
                icon={MapPin}
                title="Addresses"
                action={(
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setAddressDialog({ mode: 'add' })}
                    data-testid="add-address-btn"
                  >
                    <Plus className="h-3 w-3 mr-1" /> Add Address
                  </Button>
                )}
              />
              {data.addresses.length === 0 ? (
                <p className="text-xs text-zinc-500 italic px-2 py-3">No addresses recorded. Click <b>Add Address</b> to create the first one.</p>
              ) : (
                <div className="space-y-2">
                  {data.addresses.map(a => (
                    <div key={a.id} className="bg-zinc-50 rounded-lg p-2.5 text-sm flex items-start justify-between gap-2" data-testid={`address-row-${a.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="font-medium">{a.line1}{a.line2 ? `, ${a.line2}` : ''}</p>
                          {a.is_primary && <Badge className="bg-[#F56F05] text-white text-[9px]" data-testid={`primary-badge-${a.id}`}>Primary</Badge>}
                          {a.label && <Badge variant="outline" className="text-[9px]">{a.label}</Badge>}
                        </div>
                        <p className="text-zinc-500">{a.city}, {a.state} {a.zip}</p>
                        {a.access_notes && <p className="text-xs text-zinc-400 mt-1">Access: {a.access_notes}</p>}
                        {a.gate_code && <p className="text-xs text-zinc-400">Gate: {a.gate_code}{a.lockbox_code ? ` · Lockbox: ${a.lockbox_code}` : ''}</p>}
                        {a.pet_notes && <p className="text-xs text-zinc-400">Pets: {a.pet_notes}</p>}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {!a.is_primary && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2 text-xs"
                            title="Set as primary"
                            onClick={async () => {
                              try {
                                await axios.post(`${API_URL}/api/admin/crm/addresses/${a.id}/set-primary`, {}, { headers });
                                toast.success('Set as primary address');
                                fetchProfile();
                              } catch (err) { toast.error(err.response?.data?.detail || 'Failed to set primary'); }
                            }}
                            data-testid={`set-primary-${a.id}`}
                          >
                            <Star className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-xs"
                          title="Edit"
                          onClick={() => setAddressDialog({ mode: 'edit', address: a })}
                          data-testid={`edit-address-${a.id}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <RecordActions
                          canEdit={false}
                          canDelete={true}
                          deleteUrl={`/api/admin/crm/addresses/${a.id}`}
                          deleteConfirmTitle="Delete this address?"
                          deleteConfirmDescription="The address is removed from this customer. If any upcoming appointments use it, you'll be asked to reassign them first."
                          onDeleted={fetchProfile}
                          headers={headers}
                          testIdPrefix="address"
                          entityId={a.id}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="history" className="mt-3">
            {data.appointments.length === 0 ? (
              <p className="text-sm text-zinc-500 text-center py-6">No service history yet</p>
            ) : (
              <div className="space-y-1.5 max-h-96 overflow-y-auto">
                {data.appointments.map(a => (
                  <PastCleanRow
                    key={a.id}
                    appt={a}
                    headers={headers}
                    enums={enums}
                    onChanged={fetchProfile}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="invoices" className="mt-3">
            {data.invoices.length === 0 ? (
              <p className="text-sm text-zinc-500 text-center py-6">No invoices yet</p>
            ) : (
              <div className="space-y-1.5">
                {data.invoices.map(inv => (
                  <div key={inv.id} className="flex items-center justify-between bg-zinc-50 rounded-lg px-3 py-2 text-sm">
                    <div>
                      <p className="font-medium">${inv.total?.toFixed(2)}</p>
                      <p className="text-xs text-zinc-500">{new Date(inv.created_at).toLocaleDateString()} · {inv.payment_source || 'Stripe'}</p>
                    </div>
                    <Badge variant="outline" className={`text-xs ${inv.status === 'Paid' ? 'bg-green-50 text-green-700' : inv.status === 'Void' ? 'bg-zinc-100 text-zinc-500' : 'bg-amber-50 text-amber-700'}`}>{inv.status}</Badge>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="photos" className="mt-3">
            {data.photos.length === 0 ? (
              <p className="text-sm text-zinc-500 text-center py-6">No photos uploaded yet</p>
            ) : (
              <div className="grid grid-cols-4 gap-2 max-h-96 overflow-y-auto">
                {data.photos.map(ph => (
                  <div key={ph.id} className="aspect-square rounded-lg overflow-hidden bg-zinc-100 relative group">
                    <img src={ph.photo_url} alt={ph.photo_type} className="w-full h-full object-cover" />
                    <Badge className="absolute top-1 left-1 text-[9px] bg-black/70 text-white">{ph.photo_type}</Badge>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="notes" className="mt-3 space-y-3">
            <div className="flex gap-2">
              <Textarea rows={2} placeholder="Add internal note..." value={noteBody} onChange={e => setNoteBody(e.target.value)} data-testid="note-input" />
              <Button onClick={addNote} disabled={savingNote || !noteBody.trim()} className="bg-[#F56F05] hover:bg-[#C2410C] text-white self-start" data-testid="note-submit">
                {savingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              </Button>
            </div>
            {data.notes.length === 0 ? (
              <p className="text-sm text-zinc-500 text-center py-6">No notes yet</p>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {data.notes.map(n => (
                  <div key={n.id} className="bg-zinc-50 rounded-lg p-3 text-sm flex items-start justify-between gap-2" data-testid={`note-row-${n.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-zinc-700 whitespace-pre-wrap break-words">{n.body}</p>
                      <p className="text-xs text-zinc-400 mt-1.5">{n.created_by_name} · {new Date(n.created_at).toLocaleString()}</p>
                    </div>
                    <RecordActions
                      canEdit={false}
                      canDelete={true}
                      deleteUrl={`/api/admin/crm/internal-notes/${n.id}`}
                      deleteConfirmTitle="Delete this note?"
                      deleteConfirmDescription="This internal note will be permanently removed. The deletion is recorded in the audit log."
                      onDeleted={fetchProfile}
                      headers={headers}
                      testIdPrefix="note"
                      entityId={n.id}
                    />
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {editOpen && (
          <EditCustomerDialog
            customer={p}
            enums={enums}
            headers={headers}
            onClose={() => setEditOpen(false)}
            onSaved={async () => {
              setEditOpen(false);
              await fetchProfile();
              onRefresh && onRefresh();
              toast.success('Customer updated');
            }}
          />
        )}

        {addressDialog && (
          <AddressEditDialog
            mode={addressDialog.mode}
            userId={clientUserId}
            address={addressDialog.address}
            headers={headers}
            onClose={() => setAddressDialog(null)}
            onSaved={async () => {
              setAddressDialog(null);
              await fetchProfile();
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export const EditCustomerDialog = ({ customer, enums, headers, onClose, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    full_name: customer.user_name || '',
    email: customer.user_email && !customer.user_email.includes('@gleamgurus.internal') ? customer.user_email : '',
    phone: customer.user_phone || '',
    preferred_contact_method: customer.preferred_contact_method || '',
    lead_source: customer.lead_source || '',
    lead_source_notes: customer.lead_source_notes || '',
    client_type: customer.client_type || '',
    cleaning_frequency: customer.cleaning_frequency || '',
    agreed_price: customer.agreed_price ?? '',
    status: (customer.status || 'active').toLowerCase() === 'inactive' ? 'inactive' : 'active',
    internal_notes: customer.internal_notes || '',
    special_instructions: customer.special_instructions || '',
    cleaning_preferences: customer.cleaning_preferences || '',
    property_manager_name: customer.property_manager_name || '',
    property_manager_phone: customer.property_manager_phone || '',
    property_manager_email: customer.property_manager_email || '',
  });
  const [photoUrl, setPhotoUrl] = useState(customer.user_photo_url || '');
  const [photoTouched, setPhotoTouched] = useState(false);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Photo must be under 2MB'); return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setPhotoUrl(reader.result);
      setPhotoTouched(true);
    };
    reader.readAsDataURL(file);
  };

  const removePhoto = () => {
    setPhotoUrl('');
    setPhotoTouched(true);
  };

  const submit = async () => {
    if (!form.full_name.trim()) {
      toast.error('Full name is required'); return;
    }
    setSaving(true);
    try {
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        if (v !== '' && v !== null && v !== undefined) payload[k] = v;
      });
      if (payload.agreed_price !== undefined) payload.agreed_price = parseFloat(payload.agreed_price);
      if (photoTouched) payload.photo_url = photoUrl || '';
      await axios.put(`${API_URL}/api/admin/crm/clients/${customer.user_id}`, payload, { headers });
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-customer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-4 w-4 text-[#F56F05]" /> Edit Customer
          </DialogTitle>
          <DialogDescription>Update every field on this customer. Changes update the existing record — history, invoices, and card status remain attached.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* PROFILE PHOTO */}
          <div className="flex items-center gap-4 bg-zinc-50 rounded-lg p-3">
            <div className="relative">
              {photoUrl ? (
                <img src={photoUrl} alt="Customer" className="h-16 w-16 rounded-full object-cover border-2 border-white shadow" data-testid="edit-photo-preview" />
              ) : (
                <div className="h-16 w-16 rounded-full bg-[#F56F05]/10 border-2 border-white shadow flex items-center justify-center text-[#F56F05] font-bold">
                  {(form.full_name || 'GG').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-zinc-700 mb-1">Profile Photo</p>
              <div className="flex items-center gap-2 flex-wrap">
                <label className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-zinc-300 bg-white hover:bg-zinc-50 cursor-pointer" data-testid="upload-photo-label">
                  <Camera className="h-3 w-3" /> {photoUrl ? 'Replace' : 'Upload'}
                  <input type="file" accept="image/*" className="hidden" onChange={handlePhotoSelect} data-testid="upload-photo-input" />
                </label>
                {photoUrl && (
                  <Button size="sm" variant="ghost" onClick={removePhoto} className="text-xs h-7 text-red-600 hover:text-red-700" data-testid="remove-photo-btn">
                    <X className="h-3 w-3 mr-1" /> Remove
                  </Button>
                )}
              </div>
              <p className="text-[10px] text-zinc-400 mt-1">JPG/PNG under 2MB. Falls back to initials if no photo.</p>
            </div>
          </div>

          {/* CONTACT */}
          <div>
            <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-2">Contact</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Full Name *</Label>
                <Input value={form.full_name} onChange={e => upd('full_name', e.target.value)} data-testid="edit-name" />
              </div>
              <div>
                <Label className="text-xs">Phone</Label>
                <Input value={form.phone} onChange={e => upd('phone', e.target.value)} data-testid="edit-phone" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Email</Label>
                <Input type="email" value={form.email} onChange={e => upd('email', e.target.value)} placeholder="leave blank to keep placeholder" data-testid="edit-email" />
              </div>
              <div>
                <Label className="text-xs">Preferred Contact</Label>
                <Select value={form.preferred_contact_method} onValueChange={v => upd('preferred_contact_method', v)}>
                  <SelectTrigger data-testid="edit-preferred-contact"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{enums.contact_methods.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={form.status} onValueChange={v => upd('status', v)}>
                  <SelectTrigger data-testid="edit-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* ATTRIBUTION */}
          <div>
            <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-2">Attribution & Segmentation</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Lead Source</Label>
                <Select value={form.lead_source} onValueChange={v => upd('lead_source', v)}>
                  <SelectTrigger data-testid="edit-lead-source"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{enums.lead_sources.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Customer Type</Label>
                <Select value={form.client_type} onValueChange={v => upd('client_type', v)}>
                  <SelectTrigger data-testid="edit-client-type"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{enums.client_types.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Lead Source Details / Referred By</Label>
                <Input value={form.lead_source_notes} onChange={e => upd('lead_source_notes', e.target.value)} placeholder='e.g., "Referred by Sarah from Alberta St"' data-testid="edit-lead-source-notes" />
              </div>
            </div>
          </div>

          {/* SERVICE PREFS */}
          <div>
            <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-2">Service Preferences</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Cleaning Frequency</Label>
                <Select value={form.cleaning_frequency} onValueChange={v => upd('cleaning_frequency', v)}>
                  <SelectTrigger data-testid="edit-frequency"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{enums.cleaning_frequencies.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Agreed Price ($)</Label>
                <Input type="number" step="0.01" value={form.agreed_price} onChange={e => upd('agreed_price', e.target.value)} data-testid="edit-agreed-price" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Cleaning Preferences</Label>
                <Textarea rows={2} value={form.cleaning_preferences} onChange={e => upd('cleaning_preferences', e.target.value)} placeholder="e.g., fragrance-free products, focus on kitchen, skip guest room" data-testid="edit-cleaning-prefs" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Special Instructions</Label>
                <Textarea rows={2} value={form.special_instructions} onChange={e => upd('special_instructions', e.target.value)} placeholder="e.g., alarm code, dog in yard, do not touch office" data-testid="edit-special-instructions" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Internal Customer Notes</Label>
                <Textarea rows={2} value={form.internal_notes} onChange={e => upd('internal_notes', e.target.value)} placeholder="Private notes visible to admin/staff only" data-testid="edit-internal-notes" />
              </div>
            </div>
          </div>

          {/* PROPERTY MANAGER */}
          <div>
            <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-2">Property Manager (Optional)</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs">PM Name</Label>
                <Input value={form.property_manager_name} onChange={e => upd('property_manager_name', e.target.value)} data-testid="edit-pm-name" />
              </div>
              <div>
                <Label className="text-xs">PM Phone</Label>
                <Input value={form.property_manager_phone} onChange={e => upd('property_manager_phone', e.target.value)} data-testid="edit-pm-phone" />
              </div>
              <div>
                <Label className="text-xs">PM Email</Label>
                <Input type="email" value={form.property_manager_email} onChange={e => upd('property_manager_email', e.target.value)} data-testid="edit-pm-email" />
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t mt-4 sticky bottom-0 bg-white">
          <Button variant="outline" onClick={onClose} data-testid="edit-customer-cancel">Cancel</Button>
          <Button className="bg-[#F56F05] hover:bg-[#C2410C] text-white" onClick={submit} disabled={saving} data-testid="edit-customer-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Pencil className="h-4 w-4 mr-2" />} Save Changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============ Address Edit / Create Dialog ============

export const AddressEditDialog = ({ mode, userId, address, headers, onClose, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    line1: address?.line1 || '',
    line2: address?.line2 || '',
    city: address?.city || '',
    state: address?.state || '',
    zip: address?.zip || '',
    label: address?.label || '',
    property_type: address?.property_type || '',
    access_notes: address?.access_notes || '',
    parking_notes: address?.parking_notes || '',
    gate_code: address?.gate_code || '',
    lockbox_code: address?.lockbox_code || '',
    pet_notes: address?.pet_notes || '',
    is_primary: address?.is_primary || false,
  });
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.line1.trim() || !form.city.trim() || !form.state.trim() || !form.zip.trim()) {
      toast.error('Street, city, state and ZIP are required'); return;
    }
    setSaving(true);
    try {
      if (mode === 'add') {
        await axios.post(`${API_URL}/api/admin/crm/addresses`, { ...form, user_id: userId }, { headers });
        toast.success('Address added');
      } else {
        await axios.patch(`${API_URL}/api/admin/crm/addresses/${address.id}`, form, { headers });
        toast.success('Address updated');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save address');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="address-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-[#F56F05]" /> {mode === 'add' ? 'Add Address' : 'Edit Address'}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 mt-2">
          <div className="col-span-2">
            <Label className="text-xs">Street Address *</Label>
            <Input value={form.line1} onChange={e => upd('line1', e.target.value)} data-testid="addr-line1" />
          </div>
          <div>
            <Label className="text-xs">Unit / Apt #</Label>
            <Input value={form.line2} onChange={e => upd('line2', e.target.value)} data-testid="addr-line2" />
          </div>
          <div>
            <Label className="text-xs">Label</Label>
            <Input value={form.label} onChange={e => upd('label', e.target.value)} placeholder="Home / Office" data-testid="addr-label" />
          </div>
          <div>
            <Label className="text-xs">City *</Label>
            <Input value={form.city} onChange={e => upd('city', e.target.value)} data-testid="addr-city" />
          </div>
          <div>
            <Label className="text-xs">State *</Label>
            <Input value={form.state} onChange={e => upd('state', e.target.value)} data-testid="addr-state" />
          </div>
          <div>
            <Label className="text-xs">ZIP *</Label>
            <Input value={form.zip} onChange={e => upd('zip', e.target.value)} data-testid="addr-zip" />
          </div>
          <div>
            <Label className="text-xs">Property Type</Label>
            <Input value={form.property_type} onChange={e => upd('property_type', e.target.value)} placeholder="House / Apartment / Office" data-testid="addr-property-type" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Access Instructions</Label>
            <Textarea rows={2} value={form.access_notes} onChange={e => upd('access_notes', e.target.value)} data-testid="addr-access-notes" />
          </div>
          <div>
            <Label className="text-xs">Gate Code</Label>
            <Input value={form.gate_code} onChange={e => upd('gate_code', e.target.value)} data-testid="addr-gate" />
          </div>
          <div>
            <Label className="text-xs">Lockbox Code</Label>
            <Input value={form.lockbox_code} onChange={e => upd('lockbox_code', e.target.value)} data-testid="addr-lockbox" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Parking Notes</Label>
            <Input value={form.parking_notes} onChange={e => upd('parking_notes', e.target.value)} data-testid="addr-parking" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Pet Notes</Label>
            <Input value={form.pet_notes} onChange={e => upd('pet_notes', e.target.value)} data-testid="addr-pets" />
          </div>
          <label className="col-span-2 flex items-center gap-2 text-xs text-zinc-700 cursor-pointer">
            <input type="checkbox" checked={form.is_primary} onChange={e => upd('is_primary', e.target.checked)} data-testid="addr-primary-checkbox" />
            Set as primary address (unsets any other primary)
          </label>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#F56F05] hover:bg-[#C2410C] text-white" onClick={submit} disabled={saving} data-testid="addr-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <MapPin className="h-4 w-4 mr-2" />} {mode === 'add' ? 'Add Address' : 'Save Address'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============ Lead Source Analytics Dialog ============

const LeadSourceAnalyticsDialog = ({ headers, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(90);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API_URL}/api/admin/crm/analytics/lead-sources?period_days=${period}`, { headers })
      .then(r => setData(r.data))
      .catch(err => { toast.error('Failed to load analytics'); console.warn(err); })
      .finally(() => setLoading(false));
  }, [period, headers]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><BarChart3 className="h-5 w-5 text-[#F56F05]" /> Lead Source Performance</DialogTitle>
          <DialogDescription>Which channels are driving the most revenue?</DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 mt-3">
          {[30, 90, 180, 365].map(d => (
            <Button key={d} size="sm" variant={period === d ? 'default' : 'outline'} className={period === d ? 'bg-[#F56F05] hover:bg-[#C2410C] text-white' : ''} onClick={() => setPeriod(d)}>
              {d}d
            </Button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-[#F56F05]" /></div>
        ) : data && data.sources.length === 0 ? (
          <div className="text-center py-12">
            <AlertCircle className="h-10 w-10 text-zinc-300 mx-auto mb-2" />
            <p className="text-sm text-zinc-500">No lead source data in this period</p>
          </div>
        ) : data && (
          <div className="space-y-2 mt-4">
            {data.sources.map(row => {
              const maxRev = Math.max(...data.sources.map(r => r.total_revenue_paid), 1);
              return (
                <div key={row.lead_source} className="bg-zinc-50 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="font-semibold text-sm text-zinc-900">{row.lead_source}</span>
                    <span className="text-sm font-bold text-[#F56F05]">${row.total_revenue_paid.toFixed(2)}</span>
                  </div>
                  <div className="h-2 bg-zinc-200 rounded-full overflow-hidden mb-2">
                    <div className="h-full bg-gradient-to-r from-[#F56F05] to-orange-400" style={{ width: `${(row.total_revenue_paid / maxRev) * 100}%` }} />
                  </div>
                  <div className="flex justify-between text-xs text-zinc-500">
                    <span>{row.client_count} customer{row.client_count !== 1 ? 's' : ''}</span>
                    <span>{row.completed_jobs} job{row.completed_jobs !== 1 ? 's' : ''}</span>
                    <span>Avg ${row.avg_ticket.toFixed(2)}</span>
                    {row.unpaid_balance > 0 && <span className="text-amber-600">${row.unpaid_balance.toFixed(2)} unpaid</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AdminClientsPage;
